/**
 * Loan Intake Wizard Page
 *
 * Templated, versioned intake flow for loan applications.
 * Uses the IntakeWizard component from design-system-ui with templates
 * from the system template library.
 *
 * Route: /apply/loan?type=bridge (or construction, rental, commercial, general)
 *
 * Behavior:
 * - Authenticated users: Shows full layout with navbar and sidebar
 * - Public users: Shows standalone page with branded header
 *
 * Issue #432 - Templated Kiavi-Style Loan Intake Wizard
 */

import AddIcon from '@mui/icons-material/Add';
import BackIcon from '@mui/icons-material/ArrowBack';
import HubIcon from '@mui/icons-material/Hub';
import {
  AppBar,
  AppNavBar,
  Box,
  baseColors,
  Container,
  GlassAlert,
  GlassButton,
  GlassCard,
  GlassChip,
  IntakeWizard,
  type IntakeWizardRef,
  IntakeWizardScroll,
  type IntakeWizardScrollRef,
  iconSizes,
  NavigationProgressBar,
  NavigationProgressProvider,
  ShimmerLoader,
  SmsOptInDisclosure,
  Stack,
  Toolbar,
  Typography,
  UnifiedSidebar,
  useDesignSystem,
  useI18n,
  useNavigationProgress,
  useSmsOptInConsent,
} from '@ops-matrix/design-system-ui';
import {
  getConsumerNavConfig,
  IntakeDisplayMode,
  type IntakeFlowType,
  IntakeUrlParams,
} from '@ops-matrix/shared-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { SearchButton } from '../../components/CommandPalette';
import { PageTemplate } from '../../components/layout/PageTemplate';
import { AstorAvatar } from '../../components/loans/AstorAvatar';
import { AstorStepper } from '../../components/loans/AstorStepper';
import { useConsumerAuth } from '../../contexts/ConsumerAuthContext';
import { useConsumerType } from '../../contexts/ConsumerTypeContext';
import { useIntakeTemplate } from '../../hooks/useIntakeTemplate';
import {
  useBusinessBranding,
  useInvestmentTypeConfig,
  usePrefillData,
  useRecordReferralClick,
  useSubmitApplication,
  useValidateReferralCode,
} from '../../lib/trpc/hooks';
import { getReferralMetadata } from '../../lib/utils/referral-tracking';
import { filterTemplateInvestmentTypes } from '../../utils/filterTemplateInvestmentTypes';

// Storage key for tracking template version used
const STORAGE_KEY_TEMPLATE_VERSION = 'opsmatrix_loan_intake_template_version';

const DRAWER_WIDTH = 260;

/**
 * Public Header - White-labeled header showing business branding
 * Issue #522 - Display inviting business's name and logo
 */
function PublicHeader({ businessId }: { businessId: string | null }) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { data: branding } = useBusinessBranding(businessId);

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }} onClick={() => navigate('/')}>
          {branding?.logo ? (
            <Box
              component="img"
              src={branding.logo}
              alt={branding.name}
              sx={{
                height: 32,
                maxWidth: 120,
                objectFit: 'contain',
              }}
            />
          ) : (
            <HubIcon color="primary" sx={{ fontSize: iconSizes.xl }} />
          )}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.2 }}>
              {branding?.name || t('consumer.publicHeader.defaultName')}
            </Typography>
            {branding?.name && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('consumer.publicHeader.poweredBy')}
              </Typography>
            )}
          </Box>
        </Box>
        <GlassButton variant="outlined" size="small" onClick={() => navigate('/login')}>
          Sign In
        </GlassButton>
      </Toolbar>
    </AppBar>
  );
}

/**
 * Authenticated Layout Wrapper - Full layout with navbar and sidebar.
 *
 * This page bypasses `PortalLayout` (uses `UnifiedSidebar` directly), so it
 * mounts its own `NavigationProgressProvider` + `<NavigationProgressBar />`.
 * All other authenticated consumer pages go through PageTemplate → PortalLayout,
 * which auto-mounts both.
 */
function AuthenticatedLayout(props: { children: React.ReactNode; pageTitle?: string; pageDescription?: string }) {
  const location = useLocation();
  return (
    <NavigationProgressProvider pathKey={location.pathname + location.search}>
      <AuthenticatedLayoutInner {...props} />
    </NavigationProgressProvider>
  );
}

function AuthenticatedLayoutInner({
  children,
  pageTitle,
  pageDescription,
}: {
  children: React.ReactNode;
  pageTitle?: string;
  pageDescription?: string;
}) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { consumer, logout } = useConsumerAuth();
  const { consumerType } = useConsumerType();
  const { mode, toggleMode } = useDesignSystem();
  const { startProgress } = useNavigationProgress();

  const sidebarConfig = getConsumerNavConfig(consumerType);

  const handleSidebarToggle = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  const handleNavigate = useCallback(
    (path: string) => {
      if (location.pathname !== path) {
        startProgress();
      }
      navigate(path);
    },
    [location.pathname, navigate, startProgress],
  );

  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/login');
  }, [logout, navigate]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <NavigationProgressBar />
      <UnifiedSidebar
        config={sidebarConfig}
        pathname={location.pathname}
        open={sidebarOpen}
        onToggle={handleSidebarToggle}
        onNavigate={handleNavigate}
      />

      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
        }}
      >
        <AppNavBar
          showMenuButton
          onMenuClick={handleSidebarToggle}
          pageTitle={pageTitle}
          pageDescription={pageDescription}
          showSearch
          searchComponent={<SearchButton />}
          showThemeToggle
          themeMode={mode}
          simpleThemeToggle
          onThemeToggle={toggleMode}
          user={consumer ? { name: consumer.name, email: consumer.email } : undefined}
          onLogout={handleLogout}
          onProfile={() => navigate('/profile')}
          onSettings={() => navigate('/settings')}
          position="sticky"
          sidebarWidth={DRAWER_WIDTH}
          elevation={1}
        />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            bgcolor: 'background.default',
            minHeight: 'calc(100vh - 64px)',
          }}
        >
          <Container maxWidth="lg" sx={{ py: 3, px: { xs: 2, sm: 3 } }}>
            {children}
          </Container>
        </Box>
      </Box>
    </Box>
  );
}

/**
 * Public Layout Wrapper - Standalone page with branded header
 */
function PublicLayout({ children, businessId }: { children: React.ReactNode; businessId: string | null }) {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <PublicHeader businessId={businessId} />
      <Box sx={{ py: { xs: 2, md: 4 } }}>{children}</Box>
    </Box>
  );
}

/**
 * Main Loan Intake Wizard Page
 */
export default function LoanIntakeWizard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading: authLoading, redirectToLogin, consumer } = useConsumerAuth();
  const wizardRef = useRef<IntakeWizardRef>(null);
  const scrollRef = useRef<IntakeWizardScrollRef>(null);
  // Get template from URL query params
  // Priority: templateId (explicit) > type (by category) > 'general' (default)
  const templateIdFromUrl = searchParams.get(IntakeUrlParams.TEMPLATE_ID);
  const templateTypeFromUrl = (searchParams.get(IntakeUrlParams.TYPE) as IntakeFlowType) || 'general';

  // Pre-fill token support (Issue #475 - Lead Conversion & Partner Referral)
  const prefillToken = searchParams.get(IntakeUrlParams.PREFILL_TOKEN);
  const leadIdFromUrl = searchParams.get(IntakeUrlParams.LEAD_ID);
  const { data: prefillData, isLoading: prefillLoading } = usePrefillData(prefillToken);

  // Use prefill data for template if available, otherwise use URL params
  // Priority: intakeType (explicit) > investmentType (from form data) > URL type param > 'general'
  // This ensures the correct template loads when a lead has investmentType in their form data
  const templateId = prefillData?.data?.templateId || templateIdFromUrl;
  // investmentType comes from parsed prefill JSON (dynamic) so cast as Record<string, unknown>
  const prefillInvestmentType = (prefillData?.data as Record<string, unknown> | null)?.investmentType as
    | IntakeFlowType
    | undefined;
  const templateType =
    (prefillData?.data?.intakeType as IntakeFlowType) || prefillInvestmentType || templateTypeFromUrl;

  // Get transferred form data from navigation state (when switching between templates)
  const transferredFormData = (location.state as { formData?: Record<string, unknown> } | null)?.formData;

  // Generate storage key for localStorage persistence
  // Include leadId when present so each lead has its own storage (prevents stale data issues)
  // Without leadId, just use template.id for general browsing
  const getStorageKey = useCallback(
    (templateId: string) => {
      const baseKey = `opsmatrix_loan_intake_${templateId}`;
      return leadIdFromUrl ? `${baseKey}_${leadIdFromUrl}` : baseKey;
    },
    [leadIdFromUrl],
  );

  // Clear stale investmentType from localStorage on mount
  // This ensures the form always starts with the correct template type from URL
  // User can then change it, which will navigate to the correct template
  useEffect(() => {
    // Build the storage key pattern to check all template types
    const templateTypes = ['general', 'bridge', 'construction', 'rental', 'commercial'];
    for (const type of templateTypes) {
      const possibleKey = leadIdFromUrl
        ? `opsmatrix_loan_intake_tpl-intake-${type}_${leadIdFromUrl}`
        : `opsmatrix_loan_intake_tpl-intake-${type}`;
      try {
        const saved = localStorage.getItem(possibleKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed.formData?.investmentType && parsed.formData.investmentType !== templateType) {
            // Clear mismatched investmentType but keep other data
            delete parsed.formData.investmentType;
            localStorage.setItem(possibleKey, JSON.stringify(parsed));
          }
        }
      } catch {
        // Ignore errors
      }
    }
  }, [leadIdFromUrl, templateType]);

  // Prefill user data - priority: transferred data > prefill data > authenticated user > URL params
  const initialValues = useMemo(() => {
    const values: Record<string, unknown> = {
      // Pre-select investment type based on URL param or prefill data
      investmentType: templateType,
    };

    // First, apply authenticated user data
    if (isAuthenticated && consumer) {
      // Split full name into first/last
      const nameParts = (consumer.name || '').trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      values.firstName = firstName;
      values.lastName = lastName;
      values.email = consumer.email || '';
      values.phone = consumer.phone || '';
    }

    // Then, apply prefill data (overrides authenticated user data for pre-filled fields)
    // This allows lead/partner data to take precedence
    if (prefillData?.found && prefillData.data) {
      const pData = prefillData.data as Record<string, unknown>;
      // Only copy non-null/undefined values from prefill
      for (const [key, val] of Object.entries(pData)) {
        if (val !== null && val !== undefined && key !== 'templateId' && key !== 'intakeType' && key !== 'leadId') {
          values[key] = val;
        }
      }
    }

    // Finally, apply transferred form data (when switching templates)
    // This preserves user input like name, email, phone, address across template switches
    // IMPORTANT: Only transfer truly common fields to avoid out-of-range value errors
    // (e.g., propertyType='sfr' from residential doesn't exist in commercial template)
    if (transferredFormData) {
      // List of fields that are safe to transfer between all templates
      const commonFields = new Set([
        // Contact info (same across all templates)
        'firstName',
        'lastName',
        'email',
        'phone',
        'preferredContactMethod',
        'bestTimeToContact',
        // Address fields (same across all templates)
        'propertyAddress',
        'propertyCity',
        'propertyState',
        'propertyZip',
        // Entity info (same across all templates)
        'entityType',
        'entityName',
        // Financial amounts (same meaning across templates)
        'purchasePrice',
        'requestedLoanAmount',
        'estimatedLoanAmount',
        'downPayment',
        // Experience (same across all templates)
        'experienceLevel',
        'flipsInLast2Years',
        // Guarantor info
        'hasAdditionalGuarantors',
        'guarantorFirstName',
        'guarantorLastName',
        'guarantorEmail',
        'guarantorPhone',
      ]);

      for (const [key, val] of Object.entries(transferredFormData)) {
        // Only transfer known common fields, skip template-specific fields like propertyType
        if (val !== null && val !== undefined && commonFields.has(key)) {
          values[key] = val;
        }
      }
    }

    return values;
  }, [isAuthenticated, consumer, templateType, prefillData, transferredFormData]);

  // Business attribution - identifies which business created/shared this link
  // This is critical for multi-tenancy: applications are routed to the correct business
  // Priority: URL param > authenticated consumer's businessId
  const businessIdFromUrl =
    searchParams.get(IntakeUrlParams.BUSINESS_ID) || searchParams.get(IntakeUrlParams.BUSINESS_ID_ALT) || null;
  const effectiveBusinessId = businessIdFromUrl || consumer?.businessId || null;

  // Load investment type configuration for this business (Issue #526)
  // This determines which investment types are shown in the wizard
  const { data: investmentTypeConfig } = useInvestmentTypeConfig(effectiveBusinessId);

  // Load template - prefer templateId if provided, also pass businessId for custom templates
  const {
    template: rawTemplate,
    isLoading: templateLoading,
    error: templateError,
  } = useIntakeTemplate({
    templateId: templateId || undefined,
    templateType: templateId ? undefined : templateType,
    businessId: effectiveBusinessId || undefined,
  });

  // Filter template investment type options based on business configuration (Issue #526)
  const template = useMemo(() => {
    if (!rawTemplate) return null;
    return filterTemplateInvestmentTypes(rawTemplate, investmentTypeConfig);
  }, [rawTemplate, investmentTypeConfig]);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Branding (for SmsOptInDisclosure companyName resolution) — same hook the
  // public header already calls; useBusinessBranding caches for 5 minutes so
  // the second call hits the React Query cache, no extra network round trip.
  const { data: intakeBranding } = useBusinessBranding(effectiveBusinessId);
  const smsConsent = useSmsOptInConsent();

  // Partner referral tracking (Issue #387)
  const referralCodeFromUrl =
    searchParams.get(IntakeUrlParams.REF) || searchParams.get(IntakeUrlParams.REFERRAL) || null;
  const trackingCodeFromUrl =
    searchParams.get('tc') || searchParams.get('trackingCode') || getReferralMetadata()?.trackingCode || null;
  const { data: referralValidation } = useValidateReferralCode(referralCodeFromUrl);
  const submitApplicationMutation = useSubmitApplication();
  const recordClickMutation = useRecordReferralClick();

  // Capture UTM params for marketing attribution
  const utmParams = useMemo(
    () => ({
      source: searchParams.get(IntakeUrlParams.UTM_SOURCE) || undefined,
      medium: searchParams.get(IntakeUrlParams.UTM_MEDIUM) || undefined,
      campaign: searchParams.get(IntakeUrlParams.UTM_CAMPAIGN) || undefined,
    }),
    [searchParams],
  );

  // Record click for partner attribution
  useEffect(() => {
    if (!referralCodeFromUrl) return;

    const clickKey = `opsmatrix_click_recorded_${referralCodeFromUrl}`;
    if (sessionStorage.getItem(clickKey)) return;

    recordClickMutation.mutate(
      {
        trackingCode: referralCodeFromUrl,
        landingPage: window.location.href,
        userAgent: navigator.userAgent,
        referrer: document.referrer || undefined,
      },
      {
        onSuccess: (data) => {
          sessionStorage.setItem(clickKey, 'true');
          if (data?.clickId) {
            sessionStorage.setItem(`${clickKey}_id`, data.clickId);
          }
        },
      },
    );
  }, [referralCodeFromUrl, recordClickMutation]);

  // Check if we're inside the main Layout (via /applications/new route)
  const isInsideLayout = location.pathname.startsWith('/applications');

  // Handle auth requirement
  const handleAuthRequired = useCallback(
    (_currentStep: number, _formData: Record<string, unknown>, authMode: 'login' | 'register' = 'login') => {
      // Save form data and step to localStorage (handled by IntakeWizard)
      // Store the template version for tracking
      if (template) {
        localStorage.setItem(STORAGE_KEY_TEMPLATE_VERSION, template.data.version);
      }

      // Redirect to auth with the appropriate mode (login or register)
      redirectToLogin(undefined, undefined, authMode);
    },
    [template, redirectToLogin],
  );

  // Handle form completion
  // Helper to convert currency values (stored as numbers) to strings for API
  const toAmountString = (value: unknown): string | undefined => {
    if (value === undefined || value === null || value === '') return undefined;
    if (typeof value === 'number') return String(value);
    if (typeof value === 'string' && value.trim()) return value;
    return undefined;
  };

  const handleComplete = useCallback(
    async (formData: Record<string, unknown>) => {
      setIsSubmitting(true);
      setSubmitError(null);

      try {
        // Get clickId for partner attribution
        const clickIdKey = referralCodeFromUrl ? `opsmatrix_click_recorded_${referralCodeFromUrl}_id` : null;
        const storedClickId = clickIdKey ? sessionStorage.getItem(clickIdKey) : null;

        // Extract address components if present
        const propertyAddress = formData.propertyAddress as
          | { street1?: string; city?: string; state?: string; postalCode?: string }
          | undefined;

        // Get loan amount from various possible field names (template fields vary)
        // For general consultations, estimatedLoanAmount is a range like '500k-1m'
        const loanAmount =
          toAmountString(formData.requestedLoanAmount) ||
          toAmountString(formData.estimatedLoanAmount) ||
          toAmountString(formData.loanAmount) ||
          undefined;

        // Submit application with template tracking and full attribution
        await submitApplicationMutation.mutateAsync({
          // Business attribution (critical for multi-tenancy routing)
          businessId: effectiveBusinessId || undefined,

          // Template tracking (Issue #432)
          templateId: template?.id,
          templateVersion: template?.data.version,
          intakeType: template?.data.intakeType,

          // Raw form values (all fields for IntakeSubmissionSummary display).
          // Spread SMS opt-in consent so it lands in IntakeSubmission.formData
          // (and structured columns once those exist).
          formValues: { ...formData, ...smsConsent.payload },

          // Basic Info
          firstName: (formData.firstName as string) || '',
          lastName: (formData.lastName as string) || '',
          email: (formData.email as string) || '',
          phone: (formData.phone as string) || '',

          // Property Details
          propertyType: (formData.investmentType as string) || undefined,
          propertyAddress: propertyAddress?.street1,
          propertyCity: propertyAddress?.city,
          propertyState: propertyAddress?.state,
          propertyZip: propertyAddress?.postalCode,
          loanAmount,
          // Default loanPurpose based on intake type - 'consultation' for general, 'purchase' otherwise
          loanPurpose:
            (formData.loanPurpose as string) || (template?.data.intakeType === 'general' ? 'consultation' : 'purchase'),

          // Entity & Experience
          entityType: (formData.entityType as string) || undefined,
          flipsInLast2Years: (formData.experienceLevel as number) || undefined,

          // Financial Details
          purchasePrice: toAmountString(formData.purchasePrice),
          afterRepairValue: toAmountString(formData.afterRepairValue),
          rehabBudget: toAmountString(formData.rehabBudget),

          // Signing Date & Guarantor (if present)
          preferredSigningDate: (formData.preferredSigningDate as string) || undefined,

          // Partner Attribution (Issue #387, #390)
          referralCode: referralCodeFromUrl || undefined,
          trackingCode: trackingCodeFromUrl || undefined,
          clickId: storedClickId || undefined,
          utmSource: utmParams.source,
          utmMedium: utmParams.medium,
          utmCampaign: utmParams.campaign,

          // E-Sign confirmation (from compliance step)
          signedDocuments: formData.complianceConfirmed ? ['credit_auth', 'privacy_policy', 'econsent'] : [],
        });

        // Clear saved data on success
        localStorage.removeItem('opsmatrix_loan_intake_data');
        localStorage.removeItem('opsmatrix_loan_intake_step');
        localStorage.removeItem('opsmatrix_loan_intake_template');
        localStorage.removeItem(STORAGE_KEY_TEMPLATE_VERSION);

        // Navigate to success/dashboard
        navigate('/dashboard', {
          state: { applicationSubmitted: true, intakeType: template?.data.intakeType },
        });
      } catch (err) {
        setSubmitError(err instanceof Error ? err.message : 'Failed to submit application');
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      template,
      navigate,
      submitApplicationMutation,
      effectiveBusinessId,
      referralCodeFromUrl,
      utmParams,
      smsConsent.payload,
    ],
  );

  // Map investment type values to template types
  const investmentTypeToTemplateType: Record<string, IntakeFlowType> = useMemo(
    () => ({
      bridge: 'bridge',
      construction: 'construction',
      rental: 'rental',
      commercial: 'commercial',
      general: 'general',
    }),
    [],
  );

  // Track the last known investment type to detect actual user changes
  // Initialize with templateType since that's what the form starts with
  const lastInvestmentTypeRef = useRef<string | undefined>(templateType);
  // Track mount time to distinguish localStorage restoration from user clicks
  // localStorage restoration happens immediately on mount (<100ms), user clicks happen later
  const mountTimeRef = useRef(Date.now());

  // Handle form value changes - detect investment type switch
  const handleFormChange = useCallback(
    (values: Record<string, unknown>) => {
      const selectedInvestmentType = values.investmentType as string | undefined;
      const previousInvestmentType = lastInvestmentTypeRef.current;
      const timeSinceMount = Date.now() - mountTimeRef.current;

      // Skip navigation if this happens within 100ms of mount - likely localStorage restoration
      // User clicks take longer (mouse movement, visual processing, etc.)
      if (timeSinceMount < 100) {
        lastInvestmentTypeRef.current = selectedInvestmentType;
        return;
      }

      // Update the ref to track the current value
      lastInvestmentTypeRef.current = selectedInvestmentType;

      // Only navigate if the investment type ACTUALLY changed (user action)
      // This prevents navigation when editing other fields
      if (
        selectedInvestmentType &&
        previousInvestmentType &&
        selectedInvestmentType !== previousInvestmentType &&
        template?.data.intakeType
      ) {
        const newTemplateType = investmentTypeToTemplateType[selectedInvestmentType];

        if (newTemplateType && newTemplateType !== template.data.intakeType) {
          // Preserve ALL existing URL parameters, only update the type
          const newParams = new URLSearchParams(searchParams);
          newParams.set(IntakeUrlParams.TYPE, newTemplateType);

          // Navigate to new template - preserve current path pattern
          // If on /applications/new, stay on /applications/new
          // If on /apply/loan, stay on /apply/loan
          const basePath = isInsideLayout ? '/applications/new' : '/apply/loan';

          // Pass form data to preserve user input (name, email, phone, address, etc.)
          navigate(`${basePath}?${newParams.toString()}`, {
            replace: true,
            state: { formData: values },
          });
        }
      }
    },
    [template?.data.intakeType, investmentTypeToTemplateType, searchParams, navigate, isInsideLayout],
  );

  // Handle cancel/exit
  const handleCancel = useCallback(() => {
    // Check exit behavior from template
    const exitBehavior = template?.data.exitBehavior;

    if (exitBehavior === 'advisor_redirect' && template?.data.advisorRedirectUrl) {
      // Redirect to advisor URL
      navigate(template.data.advisorRedirectUrl);
    } else if (exitBehavior === 'custom') {
      // Custom behavior - for now just navigate home
      navigate('/');
    } else {
      // Default: case_creation - just navigate home
      navigate(isAuthenticated ? '/dashboard' : '/');
    }
  }, [template, navigate, isAuthenticated]);

  // Loading state - also wait for prefill data if token is present
  if (templateLoading || authLoading || (prefillToken && prefillLoading)) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <ShimmerLoader variant="circular" width={40} height={40} />
        <Typography color="textSecondary">{t('consumer.loan.loadingApplication')}</Typography>
      </Box>
    );
  }

  // Expired prefill token error state (Issue #475)
  if (prefillToken && prefillData && !prefillLoading && (prefillData.expired || !prefillData.found)) {
    const ExpiredContent = (
      <Box sx={{ maxWidth: 600, mx: 'auto', px: 2 }}>
        <GlassCard sx={{ p: 4, textAlign: 'center' }}>
          <GlassAlert severity="warning" sx={{ mb: 3 }}>
            {prefillData.expired
              ? 'This application link has expired. Please request a new link.'
              : 'This application link is invalid. Please request a new link.'}
          </GlassAlert>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            You can still start a new application without pre-filled data.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <GlassButton variant="outlined" startIcon={<BackIcon />} onClick={() => navigate('/')}>
              Go Back
            </GlassButton>
            <GlassButton
              variant="gradient"
              onClick={() => {
                // Remove the prefill token from URL and reload
                const newParams = new URLSearchParams(searchParams);
                newParams.delete(IntakeUrlParams.PREFILL_TOKEN);
                newParams.delete(IntakeUrlParams.LEAD_ID);
                navigate(`/apply/loan?${newParams.toString()}`);
              }}
            >
              Start New Application
            </GlassButton>
          </Box>
        </GlassCard>
      </Box>
    );

    return isAuthenticated ? (
      <AuthenticatedLayout>{ExpiredContent}</AuthenticatedLayout>
    ) : (
      <PublicLayout businessId={effectiveBusinessId}>{ExpiredContent}</PublicLayout>
    );
  }

  // Error state
  if (templateError || !template) {
    const ErrorContent = (
      <Box sx={{ maxWidth: 600, mx: 'auto', px: 2 }}>
        <GlassCard sx={{ p: 4, textAlign: 'center' }}>
          <GlassAlert severity="error" sx={{ mb: 3 }}>
            {templateError?.message || 'Unable to load the loan application form. Please try again.'}
          </GlassAlert>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            The requested application {templateId ? `(ID: ${templateId})` : `type "${templateType}"`} is not available.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <GlassButton variant="outlined" startIcon={<BackIcon />} onClick={() => navigate('/')}>
              Go Back
            </GlassButton>
            <GlassButton variant="gradient" onClick={() => window.location.reload()}>
              Try Again
            </GlassButton>
          </Box>
        </GlassCard>
      </Box>
    );

    return isAuthenticated ? (
      <AuthenticatedLayout>{ErrorContent}</AuthenticatedLayout>
    ) : (
      <PublicLayout businessId={effectiveBusinessId}>{ErrorContent}</PublicLayout>
    );
  }

  // Wizard content renderer - showHeader controls whether to show title in content area
  const renderWizardContent = (showHeader: boolean) => {
    // Determine display mode (default to scroll for better UX)
    const displayMode = template.data.displayMode ?? IntakeDisplayMode.SCROLL;
    const isScrollMode = displayMode === IntakeDisplayMode.SCROLL;

    // Borrower-portal spec wizard step indicator — 6 stages, compact circles.
    // Defaults to step 1 (Loan basics) since the template wizard manages its
    // own internal step state. We surface a stable high-level shape so the
    // borrower always sees "where am I in the overall flow".
    const wizardStages = [
      t('consumer.newLoan.step.loanBasics', { defaultValue: 'Loan basics' }),
      t('consumer.newLoan.step.property', { defaultValue: 'Property' }),
      t('consumer.newLoan.step.entity', { defaultValue: 'Entity' }),
      t('consumer.newLoan.step.financials', { defaultValue: 'Financials' }),
      t('consumer.newLoan.step.trackRecord', { defaultValue: 'Track record' }),
      t('consumer.newLoan.step.review', { defaultValue: 'Review' }),
    ];

    // Count fields Astor pre-filled vs total — derived from initialValues. This
    // is a heuristic for the banner copy until per-field provenance lands.
    const prefilledCount = Object.values(initialValues).filter((v) => v !== null && v !== undefined && v !== '').length;
    const totalFields = Math.max(prefilledCount + 4, prefilledCount); // shows "N of N+" as floor

    return (
      <>
        {/* Header - Title and Description (only for public view) */}
        {showHeader && (
          <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 3 }, mb: 3 }}>
            <Stack direction="row" spacing={1.5} sx={{ mb: 1, alignItems: 'center' }}>
              <AddIcon sx={{ color: 'text.secondary', fontSize: iconSizes.lg }} />
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {t('consumer.newLoan.title', { defaultValue: 'New loan' })}
              </Typography>
            </Stack>
            <Typography color="textSecondary" variant="body2">
              {t('consumer.newLoan.subtitle', {
                defaultValue: 'Astor pre-fills what it knows — you confirm and add the rest.',
              })}
            </Typography>
          </Box>
        )}

        {/* Spec — 6-step indicator (#1) */}
        <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 3 }, mb: 2 }}>
          <GlassCard sx={{ p: 2 }}>
            <AstorStepper
              stages={wizardStages}
              current={0}
              size="compact"
              inProgressLabel={t('consumer.newLoan.inProgress', { defaultValue: 'In progress' })}
            />
          </GlassCard>
        </Box>

        {/* Spec — Astor auto-fill banner (#2) */}
        <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 3 }, mb: 2 }}>
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              border: `1px solid ${baseColors.teal[200]}`,
              background: `linear-gradient(135deg, ${baseColors.teal[50]} 0%, transparent 70%)`,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.75,
            }}
          >
            <AstorAvatar size={28} pulse />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                {t('consumer.newLoan.astor.prefillBanner', {
                  defaultValue: 'I pre-filled {{prefilled}} of {{total}} fields from what you have shared before.',
                  prefilled: prefilledCount,
                  total: totalFields,
                })}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('consumer.newLoan.astor.prefillSub', {
                  defaultValue: 'Confirm or correct anything that has changed.',
                })}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <GlassChip
                size="small"
                label={t('consumer.newLoan.astor.confidence', { defaultValue: 'conf 92%' })}
                sx={{ bgcolor: baseColors.teal[100], color: baseColors.teal[700] }}
              />
              <GlassButton variant="text" size="small">
                {t('consumer.newLoan.astor.seeSources', { defaultValue: 'See sources' })}
              </GlassButton>
            </Stack>
          </Box>
        </Box>

        {/* Partner referral banner (Issue #387) */}
        {referralCodeFromUrl && referralValidation?.valid && (
          <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 3 }, mb: 3 }}>
            <GlassAlert severity="info" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
              {referralValidation.referrerFirstName && referralValidation.referrerCompanyName
                ? t('referral.referredByFromCompany', {
                    firstName: referralValidation.referrerFirstName,
                    companyName: referralValidation.referrerCompanyName,
                  })
                : t('referral.referredByName', { name: referralValidation.partnerName || referralCodeFromUrl })}
              {referralValidation.partnerCompanyName && (
                <>
                  {' — '}
                  {t('referral.referredToCompany', { companyName: referralValidation.partnerCompanyName })}
                </>
              )}
            </GlassAlert>
          </Box>
        )}
        {referralCodeFromUrl && referralValidation && !referralValidation.valid && (
          <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 3 }, mb: 3 }}>
            <GlassAlert severity="warning" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
              {'expired' in referralValidation ? t('referral.expired') : t('referral.invalid')}
            </GlassAlert>
          </Box>
        )}

        {/* Submission error */}
        {submitError && (
          <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 3 }, mb: 3 }}>
            <GlassAlert severity="error" onClose={() => setSubmitError(null)}>
              {submitError}
            </GlassAlert>
          </Box>
        )}

        {/* Wizard - Conditional rendering based on display mode */}
        <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 3 } }}>
          {isScrollMode ? (
            <IntakeWizardScroll
              ref={scrollRef}
              template={template}
              initialValues={initialValues}
              isAuthenticated={isAuthenticated}
              onAuthRequired={handleAuthRequired}
              onComplete={handleComplete}
              onChange={handleFormChange}
              onCancel={handleCancel}
              isSubmitting={isSubmitting}
              storageKey={getStorageKey(template.id)}
              submitFooter={<SmsOptInDisclosure companyName={intakeBranding?.name ?? undefined} {...smsConsent.bind} />}
            />
          ) : (
            <IntakeWizard
              ref={wizardRef}
              template={template}
              initialValues={initialValues}
              isAuthenticated={isAuthenticated}
              onAuthRequired={handleAuthRequired}
              onComplete={handleComplete}
              onChange={handleFormChange}
              onCancel={handleCancel}
              isSubmitting={isSubmitting}
              submitFooter={<SmsOptInDisclosure companyName={intakeBranding?.name ?? undefined} {...smsConsent.bind} />}
            />
          )}
        </Box>

        {/* Trust badges */}
        <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 3 }, mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            Your information is encrypted and secure.
          </Typography>
        </Box>
      </>
    );
  };

  // Render with appropriate layout based on auth state and route
  // isInsideLayout was determined earlier - when inside Layout, don't add our own chrome
  if (isInsideLayout && isAuthenticated) {
    // Inside Layout's Outlet - wrap in PageTemplate for header metadata
    return (
      <PageTemplate variant="form" noPadding title={template.name} description={template.description}>
        {renderWizardContent(false)}
      </PageTemplate>
    );
  }

  return isAuthenticated ? (
    <AuthenticatedLayout pageTitle={template.name} pageDescription={template.description}>
      {renderWizardContent(false)}
    </AuthenticatedLayout>
  ) : (
    <PublicLayout businessId={effectiveBusinessId}>{renderWizardContent(true)}</PublicLayout>
  );
}
